/**
 * Features Page Component
 *
 * Showcases all Armenian Link services: Links, QR Codes, Landing Pages, and Forms.
 * Designed to explain each product's value in a clear, visual format.
 *
 * @module pages/features
 */

import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowRight, BarChart3, Zap, Target, Users, Share2, Clock } from 'lucide-react';
import { cn } from '@/client/lib/utils';
import { useReducedMotion } from '@/client/hooks/use-reduced-motion';
import { getEnabledServices, getToolsHeadline, getServiceNamesList } from '@/client/lib/config';

// =============================================================================
// Feature Card Component
// =============================================================================

interface FeatureCardProps {
  icon: React.ElementType;
  title: string;
  description: string;
  features: string[];
  cta: string;
  ctaLink: string;
  gradient: string;
  delay?: number;
  available?: boolean;
}

function FeatureCard({
  icon: Icon,
  title,
  description,
  features,
  cta,
  ctaLink,
  gradient,
  delay = 0,
  available = true,
}: FeatureCardProps) {
  const prefersReducedMotion = useReducedMotion();
  const comingSoon = !available;
  return (
    <motion.div
      initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{
        delay: prefersReducedMotion ? 0 : delay,
        duration: prefersReducedMotion ? 0.01 : 0.5,
      }}
      className={cn(
        'bg-card rounded-2xl border border-border p-8 transition-all group',
        comingSoon ? 'opacity-80' : 'hover:border-primary/30'
      )}
    >
      <div className="flex items-start justify-between mb-6">
        <div
          className={cn(
            'w-14 h-14 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110 bg-gradient-to-br',
            gradient,
            comingSoon && 'opacity-75'
          )}
        >
          <Icon className="w-7 h-7 text-white" />
        </div>
        {comingSoon && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-muted text-muted-foreground text-xs font-medium">
            <Clock className="w-3 h-3" />
            Coming Soon
          </span>
        )}
      </div>

      <h3 className="text-2xl font-bold text-foreground mb-3">{title}</h3>
      <p className="text-muted-foreground mb-6 leading-relaxed">{description}</p>

      <ul className="space-y-3 mb-8">
        {features.map((feature, i) => (
          <li key={i} className="flex items-start gap-3 text-sm text-foreground/80">
            <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Zap className="w-3 h-3 text-primary" />
            </div>
            {feature}
          </li>
        ))}
      </ul>

      {comingSoon ? (
        <span className="inline-flex items-center gap-2 text-muted-foreground font-medium cursor-not-allowed">
          {cta}
        </span>
      ) : (
        <Link
          to={ctaLink}
          className="inline-flex items-center gap-2 text-primary font-medium hover:underline underline-offset-4"
        >
          {cta}
          <ArrowRight className="w-4 h-4" />
        </Link>
      )}
    </motion.div>
  );
}

// =============================================================================
// Use Case Badge
// =============================================================================

function UseCaseBadge({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-muted/50 border border-border text-sm text-muted-foreground">
      {children}
    </span>
  );
}

// =============================================================================
// Features Page
// =============================================================================

export function FeaturesPage() {
  // Get enabled services from central configuration
  const prefersReducedMotion = useReducedMotion();
  const enabledServices = getEnabledServices();
  const toolsHeadline = getToolsHeadline();
  const serviceNames = getServiceNamesList();

  return (
    <div className="bg-background min-h-screen">
      {/* Decorative background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl" />
      </div>

      {/* Hero */}
      <section className="relative pt-20 pb-16 lg:pt-32 lg:pb-24">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: prefersReducedMotion ? 0 : -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: prefersReducedMotion ? 0.01 : 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-6"
          >
            <Target className="w-4 h-4" />
            Everything you need to engage your audience
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: prefersReducedMotion ? 0 : 0.1,
              duration: prefersReducedMotion ? 0.01 : 0.6,
            }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6"
          >
            One platform.
            <br />
            <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent capitalize">
              {toolsHeadline}.
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: prefersReducedMotion ? 0 : 0.2,
              duration: prefersReducedMotion ? 0.01 : 0.6,
            }}
            className="text-xl text-muted-foreground max-w-2xl mx-auto"
          >
            {serviceNames} — all connected with analytics that show you what&apos;s working.
          </motion.p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="relative pb-24">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {enabledServices.map((service, i) => (
              <FeatureCard
                key={service.id}
                icon={service.icon}
                title={service.name}
                description={service.longDescription}
                features={service.features}
                cta={service.ctaText}
                ctaLink={service.ctaLink}
                gradient={service.gradient}
                available={service.available}
                delay={0.1 * i}
              />
            ))}
          </div>
        </div>
      </section>

      {/* How They Work Together */}
      <section className="relative py-24 border-t border-border">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: prefersReducedMotion ? 0.01 : 0.6 }}
              className="text-3xl md:text-4xl font-bold text-foreground mb-4"
            >
              Better{' '}
              <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">
                together
              </span>
            </motion.h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Each tool works independently, but they&apos;re designed to work together for maximum
              impact.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: prefersReducedMotion ? 0.01 : 0.6 }}
              className="text-center"
            >
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center mx-auto mb-4">
                <Share2 className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Share anywhere</h3>
              <p className="text-sm text-muted-foreground">
                Create a short link, generate its QR code, and embed it in your landing page — all
                from one dashboard.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                delay: prefersReducedMotion ? 0 : 0.1,
                duration: prefersReducedMotion ? 0.01 : 0.6,
              }}
              className="text-center"
            >
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Unified analytics</h3>
              <p className="text-sm text-muted-foreground">
                Whether someone clicks a link, scans a QR code, or submits a form — see it all in
                one place.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                delay: prefersReducedMotion ? 0 : 0.2,
                duration: prefersReducedMotion ? 0.01 : 0.6,
              }}
              className="text-center"
            >
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-green-500/20 flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Team collaboration</h3>
              <p className="text-sm text-muted-foreground">
                Share access with your team. Everyone can create, everyone can see the results.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="relative py-24 border-t border-border bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <motion.h2
              initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: prefersReducedMotion ? 0.01 : 0.6 }}
              className="text-3xl md:text-4xl font-bold text-foreground mb-4"
            >
              Built for{' '}
              <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">
                real-world use
              </span>
            </motion.h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Whether you&apos;re running a church, restaurant, nonprofit, or local business — these
              tools are built for you.
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: prefersReducedMotion ? 0.01 : 0.6 }}
            className="flex flex-wrap justify-center gap-3 max-w-3xl mx-auto"
          >
            <UseCaseBadge>Church bulletins</UseCaseBadge>
            <UseCaseBadge>Event registration</UseCaseBadge>
            <UseCaseBadge>Menu QR codes</UseCaseBadge>
            <UseCaseBadge>Donation campaigns</UseCaseBadge>
            <UseCaseBadge>Social media bios</UseCaseBadge>
            <UseCaseBadge>Volunteer coordination</UseCaseBadge>
            <UseCaseBadge>Print advertising</UseCaseBadge>
            <UseCaseBadge>Email newsletters</UseCaseBadge>
            <UseCaseBadge>Business cards</UseCaseBadge>
            <UseCaseBadge>Flyers & posters</UseCaseBadge>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24">
        <div className="container mx-auto px-4">
          <div className="relative max-w-3xl mx-auto text-center">
            <div className="absolute -inset-4 bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-red-500/10 rounded-3xl blur-2xl" />
            <div className="relative bg-card backdrop-blur-xl rounded-2xl border border-border p-12 shadow-lg">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Ready to get started?
              </h2>
              <p className="text-muted-foreground mb-8 max-w-lg mx-auto">
                Create your first short link right now — no account needed. When you&apos;re ready
                for more, upgrade to Plus for just $5/month.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 px-8 py-4 text-lg font-semibold text-zinc-900 transition-all hover:scale-105"
                >
                  Try It Free
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/pricing"
                  className="inline-flex items-center justify-center rounded-xl border border-border bg-muted/50 hover:bg-muted px-8 py-4 text-lg font-medium text-foreground transition-all"
                >
                  View Pricing
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
